/**
 * What this session may do — fetched once, read everywhere.
 *
 * A context rather than a hook per component, because a dozen screens ask the
 * same question and `GET /api/auth/capabilities` should be answered once per
 * session rather than once per mount. It sits inside AuthProvider, so it can
 * refetch when the session changes and clear when it ends.
 *
 * **This is a rendering aid, not a security boundary.** Every capability it
 * reports is re-checked server-side on the request that uses it; hiding a button
 * is a courtesy to the user, not a control. The house rule is stated at the top
 * of lib/aiAccess.js and it applies here unchanged.
 *
 * Which is why this fails CLOSED. `useAiAnalysis` fails open — it returns
 * `canUseAi: true` when the capabilities call errors — and the result is that
 * during an outage every account sees the AI button and only the server's
 * refusal stops them. That is defensible for one button whose backend refuses
 * anyway. It is not defensible as a general rule: a permission list that
 * defaults to "everything" while loading would flash the entire hospital admin
 * menu at a receptionist on every slow connection. So `can()` is false until an
 * answer arrives, and screens render their loading state rather than their
 * denied state while `loading` is true.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authApi } from '../api/endpoints';
import { CONTROL_REFUSED_EVENT } from '../api/client';
import { useAuth } from './AuthContext';
import { useControlChannel } from '../hooks/useControlChannel';
import { canChange, canCreate, isShown, limitOf, policyOf, stateOf } from '../domain/serviceControl';

const PermissionContext = createContext(null);

const EMPTY = Object.freeze([]);

export function PermissionProvider({ children }) {
  const { isAuthenticated, session } = useAuth();

  const [state, setState] = useState({
    permissions: EMPTY,
    institutionId: null,
    institutionName: null,
    institutionType: null,
    memberRole: null,
    roleTitle: null,
    institutionLogo: null,
    institutionLogoDark: null,
    institutionLogoBackdrop: 'none',
    institutionColor: null,
    mustChangePassword: false,
    theme: null,
    servesDoctorId: null,
    assignedRoomIds: null,
    actingAsPlatformAdmin: false,
    adminRole: null,
    platformRole: null,
    sessionId: null,
    locale: null,
    control: null,
    loading: false,
    // False until the first answer for this session (or its failure) is in.
    // `loading` alone is false for the render between signing in and the
    // request starting, when the permissions are still empty — a guard that
    // read that as "holds nothing" sent a manager to the wrong page.
    ready: false,
    error: null,
  });

  const load = useCallback(async ({ quiet = false } = {}) => {
    // A re-read pushed by the central control keeps the screen as it is
    // while it loads — `loading` would blank every permission-gated part.
    if (!quiet) setState((s) => ({ ...s, loading: true, ready: false, error: null }));
    try {
      const data = await authApi.capabilities();
      setState({
        permissions: Array.isArray(data?.permissions) ? data.permissions : EMPTY,
        institutionId: data?.institutionId ?? null,
        institutionName: data?.institutionName ?? null,
        institutionType: data?.institutionType ?? null,
        memberRole: data?.memberRole ?? null,
        roleTitle: data?.roleTitle ?? null,
        institutionLogo: data?.institutionLogo ?? null,
        institutionLogoDark: data?.institutionLogoDark ?? null,
        institutionLogoBackdrop: data?.institutionLogoBackdrop ?? 'none',
        institutionColor: data?.institutionColor ?? null,
        mustChangePassword: Boolean(data?.mustChangePassword),
        theme: data?.theme ?? null,
        servesDoctorId: data?.servesDoctorId ?? null,
        assignedRoomIds: data?.assignedRoomIds ?? null,
        actingAsPlatformAdmin: Boolean(data?.actingAsPlatformAdmin),
        // المرحلة 1.1: the administration role on top of the login role, the
        // platform team's own role, this device's session, and the languages
        // and country the screens use (i18n/LocaleSync.jsx).
        adminRole: data?.adminRole ?? null,
        platformRole: data?.platformRole ?? null,
        sessionId: data?.sessionId ?? null,
        locale: data?.locale ?? null,
        // The institution's package: { plan, controls } — see domain/serviceControl.js.
        control: data?.control ?? null,
        loading: false,
        ready: true,
        error: null,
      });
    } catch (err) {
      // A quiet re-read that fails keeps what the screen already had.
      if (quiet) return;
      // Fail closed: no capabilities until the server says otherwise. The
      // screens distinguish this from a denial by checking `loading`/`error`.
      console.error('[Permissions] Could not load capabilities:', err.message);
      setState({
        permissions: EMPTY,
        institutionId: null,
        institutionName: null,
        institutionType: null,
        memberRole: null,
        roleTitle: null,
        institutionLogo: null,
        institutionLogoDark: null,
        institutionLogoBackdrop: 'none',
        institutionColor: null,
        mustChangePassword: false,
        theme: null,
        servesDoctorId: null,
        assignedRoomIds: null,
        actingAsPlatformAdmin: false,
        adminRole: null,
        platformRole: null,
        sessionId: null,
        locale: null,
        control: null,
        loading: false,
        ready: true,
        error: err.message,
      });
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setState({
        permissions: EMPTY,
        institutionId: null,
        institutionName: null,
        institutionType: null,
        memberRole: null,
        roleTitle: null,
        institutionLogo: null,
        institutionLogoDark: null,
        institutionLogoBackdrop: 'none',
        institutionColor: null,
        mustChangePassword: false,
        theme: null,
        servesDoctorId: null,
        assignedRoomIds: null,
        actingAsPlatformAdmin: false,
        adminRole: null,
        platformRole: null,
        sessionId: null,
        locale: null,
        control: null,
        loading: false,
        ready: false,
        error: null,
      });
      return;
    }
    load();
  }, [isAuthenticated, session?.id, load]);

  // The Super Admin entering or leaving a hospital changes which hospital the
  // server resolves them into, so the answer has to be asked again.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const onChange = () => load();
    window.addEventListener('capsule:acting-institution', onChange);
    return () => window.removeEventListener('capsule:acting-institution', onChange);
  }, [isAuthenticated, load]);

  // The central control: the Super Admin's change is pushed over the live
  // socket and re-read at once; a refusal from the server (the package changed
  // under this screen) triggers the same re-read.
  const reread = useCallback(() => load({ quiet: true }), [load]);
  useControlChannel(Boolean(isAuthenticated && state.institutionId), reread);
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    window.addEventListener(CONTROL_REFUSED_EVENT, reread);
    return () => window.removeEventListener(CONTROL_REFUSED_EVENT, reread);
  }, [isAuthenticated, reread]);

  const value = useMemo(() => {
    const held = state.permissions;
    const control = state.control;
    return {
      ...state,
      /** The package's state of one control: open | limited | trial | readonly | closed | draining. */
      serviceState: (id) => stateOf(control, id),
      /** In the menu / on the screen at all? */
      serviceShown: (id) => isShown(control, id),
      /** May the screen offer to add something new? */
      serviceCreates: (id) => canCreate(control, id),
      /** May the screen offer to change what exists? */
      serviceChanges: (id) => canChange(control, id),
      servicePolicy: (id) => policyOf(control, id),
      serviceLimit: (id, metric) => limitOf(control, id, metric),
      /** Does this session hold every one of the named capabilities? */
      can: (...required) => required.flat().every((name) => held.includes(name)),
      /** At least one of them? */
      canAny: (...required) => required.flat().some((name) => held.includes(name)),
      /** Re-read after something changed a privilege. */
      refresh: load,
    };
  }, [state, load]);

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function usePermissions() {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used inside a PermissionProvider');
  }
  return context;
}

export default PermissionContext;
